import React from 'react'
import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { K1Information } from '@e3dc-react/icons'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

/**
 * Renders wallbox cell. Conditionally displays just the wallbox name in case this wallbox name is unique across all connected wallboxes.
 * Otherwise, it displays both the wallbox name and wallbox id to differentiate between two wallboxes.
 *
 * In case session was triggered on a wallbox which is no longer connected, just the wallbox id will be displayed.
 */
export const WallboxCell: React.VFC<{
  wallboxName: string | undefined
  wallboxId: string
  showId: boolean
}> = ({ wallboxName, wallboxId, showId }): React.ReactElement => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const formattedId = t('emobility.chargingSession.wallboxId', { id: wallboxId })

  if (!wallboxName) {
    return <Typography variant="body2">{formattedId}</Typography>
  }

  if (!showId) {
    return <Typography variant="body2">{wallboxName}</Typography>
  }

  /**
   * In case there are multiple wallboxes with the same name, show wallbox name, but also an "info" button
   * next to wallbox name which opens a tooltip containing the unique wallbox id.
   */
  return (
    <Stack flexDirection="row" alignItems="center">
      <Box>{wallboxName}</Box>

      <OptionalTooltipWrapper titleKey={formattedId} placement={downSm ? 'top' : 'right-start'}>
        <IconButton
          color="inherit"
          onClick={e => {
            /**
             * We want to prevent bubbling of click event up to the parent.
             * Clicking info button should only open the tooltip, regardless of click handlers in parent.
             * This will prevent unintentionally opening session details modal(by default, the whole table row is clickable).
             */
            e.stopPropagation()
          }}
        >
          <K1Information />
        </IconButton>
      </OptionalTooltipWrapper>
    </Stack>
  )
}
